import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as Popover from '@radix-ui/react-popover'
import { Bell, ChevronDown, LogOut, Menu, ShoppingBag, User } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'

import { LocaleSwitcher } from '@/app/components/LocaleSwitcher'
import { getLocalizedPath } from '@/content/i18n'
import { dashboardCopy, resolveDashboardPageKey } from '@/content/dashboardContent'
import { logout as logoutApi } from '@/services/admin/authApi'
import { useAuth } from '@/hooks/useAuth'
import { usePublicSiteBundle } from '@/hooks/usePublicSiteBundle'
import { cn, getLocalizedValue } from '@/lib/utils'
import type { Direction, Locale } from '@/types/site'

type DashboardHeaderProps = {
  locale: Locale
  direction: Direction
  onOpenMobileNav?: () => void
}

export function DashboardHeader({ locale, direction, onOpenMobileNav }: DashboardHeaderProps) {
  const location = useLocation()
  const { signOut } = useAuth()
  const siteBundle = usePublicSiteBundle()
  const storeUrl = (() => {
    const int = siteBundle.data?.site_config?.integrations
    if (int && typeof int === 'object') {
      const c = (int as Record<string, unknown>).contact
      if (c && typeof c === 'object') {
        const url = (c as Record<string, unknown>).store_url
        if (typeof url === 'string' && url.trim()) return url.trim()
      }
    }
    return null
  })()
  const pageKey = resolveDashboardPageKey(location.pathname)
  const pageTitle =
    getLocalizedValue(dashboardCopy.pageTitles[pageKey] ?? dashboardCopy.pageTitles.overview, locale) ??
    getLocalizedValue(dashboardCopy.headerTitle, locale)

  async function handleSignOut() {
    try {
      await logoutApi()
    } catch {
      /* still clear client session */
    }
    signOut()
  }

  const profilePath = getLocalizedPath(locale, 'dashboard/profile')

  return (
    <header className="z-40 flex shrink-0 items-center justify-between gap-3 border-b border-slate-200/80 bg-white/90 px-3 py-2.5 shadow-card backdrop-blur-md md:px-5 lg:px-6">
      <div className="flex min-w-0 items-center gap-3">
        {onOpenMobileNav ? (
          <button
            type="button"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200/90 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 lg:hidden"
            onClick={onOpenMobileNav}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        ) : null}
        <div className="min-w-0">
        <p className="truncate text-base font-bold text-slate-900 md:text-lg">{pageTitle}</p>
        <p className="truncate text-xs text-slate-500 md:text-sm">
          {getLocalizedValue(dashboardCopy.headerTitle, locale)}
        </p>
        </div>
      </div>

      <div
        className={cn(
          'flex shrink-0 items-center gap-1.5 sm:gap-2',
          direction === 'rtl' && 'flex-row-reverse',
        )}
      >
        {storeUrl && (
          <a
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200/90 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-brand"
            aria-label={locale === 'ar' ? 'المتجر الإلكتروني' : 'Online Store'}
            title={locale === 'ar' ? 'المتجر الإلكتروني' : 'Online Store'}
          >
            <ShoppingBag className="h-5 w-5" aria-hidden />
          </a>
        )}
        <LocaleSwitcher locale={locale} direction={direction} variant="default" mode="icon" />
        <Popover.Root>
          <Popover.Trigger asChild>
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200/90 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50"
              aria-label={getLocalizedValue(dashboardCopy.notifications, locale)}
            >
              <Bell className="h-5 w-5" aria-hidden />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              dir={direction}
              lang={locale === 'ar' ? 'ar' : 'en'}
              className="z-[80] w-72 rounded-xl border border-slate-200/90 bg-white p-3 text-start text-sm text-slate-600 shadow-float"
              sideOffset={8}
              align={direction === 'rtl' ? 'start' : 'end'}
            >
              <p className="font-semibold text-slate-800">{getLocalizedValue(dashboardCopy.notifications, locale)}</p>
              <p className="mt-2 text-xs text-slate-500">
                {getLocalizedValue(dashboardCopy.notificationsEmpty, locale)}
              </p>
              <Popover.Arrow className="fill-white" />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="inline-flex h-10 items-center gap-2 rounded-full border border-slate-200/90 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <User className="h-4 w-4 shrink-0 text-dash-accent" aria-hidden />
              <span className="hidden max-w-[8rem] truncate sm:inline">
                {getLocalizedValue(dashboardCopy.userMenu, locale)}
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="z-[80] min-w-[12rem] rounded-xl border border-slate-200/90 bg-white p-0 text-start text-sm shadow-float"
              sideOffset={8}
              align={direction === 'rtl' ? 'start' : 'end'}
            >
              <div
                dir={direction}
                lang={locale === 'ar' ? 'ar' : 'en'}
                className="p-1"
              >
                <DropdownMenu.Item asChild>
                  <Link
                    to={profilePath}
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-slate-700 outline-none hover:bg-surface-muted"
                  >
                    <User className="h-4 w-4 shrink-0" aria-hidden />
                    {getLocalizedValue(dashboardCopy.profileNav, locale)}
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-slate-700 outline-none hover:bg-surface-muted"
                  onSelect={() => {
                    void handleSignOut()
                  }}
                >
                  <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                  {getLocalizedValue(dashboardCopy.signOut, locale)}
                </DropdownMenu.Item>
              </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  )
}
